import request from 'utils/request'
import { readSessionTokens } from 'utils/session'
import {
  signIn,
  signOut,
  signUp as cognitoSignUp,
  confirmSignUp as cognitoConfirmSignUp,
  resendSignUpCode
} from 'aws-amplify/auth'

// Name of the error getCognitoSession throws when nobody is signed in.
export const NO_SESSION_ERROR = 'NoCognitoSessionError'

export function login(data) {
  return request('auth/', {
    method: 'POST',
    data
  }).then(([body]) => body)
}

/**
 * Signs the user in against Cognito.
 *
 * An unconfirmed account is not an error in Amplify v6. It comes back as
 * nextStep.signInStep === 'CONFIRM_SIGN_UP', which onLoginCognito reads.
 *
 * @param {Object} data - Login form values.
 * @param {string} data.email - Email, used as the Cognito username.
 * @param {string} data.password - Password.
 * @returns {Promise<{isSignedIn: boolean, nextStep: Object}>} The sign in result.
 * @throws Rethrows the Cognito error, for example NotAuthorizedException when
 *   the password is wrong.
 */
export async function loginCognito(data) {
  try {
    return await signIn({ username: data.email, password: data.password })
  } catch (error) {
    // Unlike v4, v6 signIn throws if a session is already cached. Clear it and
    // retry so a returning user, or a switched account, can sign in cleanly.
    if (error?.name === 'UserAlreadyAuthenticatedException') {
      await signOut()

      return signIn({ username: data.email, password: data.password })
    }

    throw error
  }
}

/**
 * Reads the tokens of the current Cognito session.
 *
 * Amplify v6 resolves with no tokens when nobody is signed in, instead of
 * throwing. This turns that into an error so the callers' catch blocks run and
 * the user is sent back to the login page.
 *
 * @returns {Promise<Object>} The session tokens, with accessToken and idToken.
 * @throws {Error} When there is no session, with the name NO_SESSION_ERROR.
 *   Other errors, such as a network error during the renewal, are rethrown as
 *   they come from Amplify.
 */
export async function getCognitoSession() {
  const tokens = await readSessionTokens()

  if (!tokens) {
    const error = new Error('No Cognito session')
    error.name = NO_SESSION_ERROR
    throw error
  }

  return tokens
}

/**
 * Signs the user out of Cognito.
 *
 * It can throw, for example on a bad Cognito config. When only revoking the
 * token in Cognito fails, Amplify still clears its local tokens.
 *
 * @returns {Promise<void>}
 */
export async function logoutCognito() {
  await signOut()
}

export function signUp(data) {
  delete data.password
  delete data.repeatPassword
  delete data.captcha
  delete data.checkboxPolicy
  delete data.autoConfirm

  return request('users', {
    method: 'POST',
    data
  })
}

export function signUpTrustedUser(data) {
  delete data.password
  delete data.repeatPassword
  delete data.captcha
  delete data.checkboxPolicy
  delete data.autoConfirm
  return request('users/trusted-user/sign-up', {
    method: 'POST',
    data
  })
}

/**
 * Creates the account in Cognito.
 *
 * Amplify v6 takes the attributes under options.userAttributes and no longer
 * returns the user, so the caller uses the submitted email as the username.
 * That is what this pool uses as the username anyway.
 *
 * @param {Object} data - Sign up form values.
 * @param {string} data.email - Email, used as the Cognito username.
 * @param {string} data.password - Password.
 * @param {string} data.language - Language, stored as the locale attribute.
 * @param {string} data.autoConfirm - Value of the custom:collaborative attribute.
 * @param {string} [data.channel] - Channel, stored as custom:channel when given.
 * @returns {Promise<{isSignUpComplete: boolean, userId: string, nextStep: Object}>}
 * @throws Rethrows the Cognito error, for example UsernameExistsException.
 */
export async function signUpCognito(data) {
  const { email, password, language, channel, autoConfirm } = data
  const userAttributes = {
    email,
    locale: language,
    'custom:collaborative': autoConfirm
  }

  if (channel) {
    userAttributes['custom:channel'] = channel
  }

  return cognitoSignUp({
    username: email,
    password,
    options: { userAttributes }
  })
}

/**
 * Confirms the account with the code Cognito mailed to the user.
 *
 * @param {Object} data - Confirmation form values.
 * @param {string} data.email - Email, used as the Cognito username.
 * @param {string} data.verificationCode - Code from the mail.
 * @returns {Promise<{isSignUpComplete: boolean, nextStep: Object}>}
 * @throws Rethrows the Cognito error, for example ExpiredCodeException.
 */
export async function confirmSignUpCognito(data) {
  const { email, verificationCode } = data

  return cognitoConfirmSignUp({ username: email, confirmationCode: verificationCode })
}

/**
 * Asks Cognito to mail a new confirmation code.
 *
 * @param {string} username - Email of the account to confirm.
 * @returns {Promise<Object>} Where the code was sent.
 * @throws Rethrows the Cognito error, for example LimitExceededException.
 */
export async function resendConfirmationMail(username) {
  return resendSignUpCode({ username })
}

export function loadUser(id) {
  return request(`users/${id}`, {
    method: 'GET'
  }).then(([body]) => body)
}
